import { __extends, __assign, __spreadArray, __rest } from 'tslib';
import React__default from 'react';
import clsx from 'clsx';
import { AutoSizer, Table as Table$1, Column, defaultTableRowRenderer } from 'react-virtualized';
import { ReactComponent as SvgDefaultTableEmptyIllustration } from '../../img/default_table_empty_illustration.svg.js';
import colors from '../../util/colors.js';
import Checkbox from '../Checkbox/Checkbox.component.js';
import IconButton from '../IconButton/IconButton.component.js';
import TableError from './TableError.component.js';
import { propTypes, defaultProps } from './Table.types.js';
import './Table.scss.js';
import styles from './Table.module.scss.js';

var isNotSafariBrowser = !/^((?!chrome|android).)*safari/i.test(navigator.userAgent);
var Table = /** @class */ (function (_super) {
    __extends(Table, _super);
    function Table() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.state = {
            // Both index and key are needed to keep track of data changes in componentDidUpdate
            expandedRowKey: null,
            expandedRowIndex: null,
            selectAll: false,
        };
        _this.tableRef = React__default.createRef();
        _this.findCurrentKeyFromExpandedRowKeys = function (index) {
            var expandedRowKey = _this.state.expandedRowKey;
            var expandedRowKeys = _this.props.expandedRowKeys;
            if (expandedRowKeys === null || expandedRowKeys === void 0 ? void 0 : expandedRowKeys.length) {
                var openedRowByIndex = expandedRowKeys.find(function (value) { return value === +index; });
                if (typeof openedRowByIndex === 'number') {
                    return openedRowByIndex.toString();
                }
            }
            return expandedRowKey;
        };
        _this.getRowClassNameByIndex = function (index) {
            if (index < 0) {
                return styles.Table_headerRow;
            }
            else {
                return index % 2 === 0
                    ? styles.Table_row___even
                    : styles.Table_row___odd;
            }
        };
        _this.defaultCollapsibleRowContentRenderer = function (_a) {
            var className = _a.className, style = _a.style, columns = _a.columns;
            return (React__default.createElement("div", { className: className, style: style }, columns));
        };
        _this.collapsibleRowRenderer = function (props) {
            var _a;
            var index = props.index, onRowClick = props.onRowClick, style = props.style, rowData = props.rowData, rowHeight = props.rowHeight, collapsibleContentHeight = props.collapsibleContentHeight, collapsibleContentGetter = props.collapsibleContentGetter, keyGetter = props.keyGetter;
            var _b = _this.props, collapsibleRowClassName = _b.collapsibleRowClassName, customRowRenderer = _b.customRowRenderer;
            var currentKey = _this.findCurrentKeyFromExpandedRowKeys(index);
            // This chunk of code comes from the defaultTableRowRenderer. Added so both renderers are consistent.
            var a11yProps = {
                'aria-rowindex': index + 1,
            };
            var tableRowRenderer = customRowRenderer || _this.defaultCollapsibleRowContentRenderer;
            if (onRowClick) {
                a11yProps['aria-label'] = 'row';
                a11yProps.tabIndex = 0;
                a11yProps.onClick = function (event) {
                    return onRowClick === null || onRowClick === void 0 ? void 0 : onRowClick({ event: event, index: index, rowData: rowData });
                };
            }
            var classNames = clsx(styles.Collapsible, isNotSafariBrowser && styles.Collapsible___withTransition, collapsibleRowClassName);
            var isRowCollapsible = _this.props.isCollapsible &&
                !((_a = _this.props.nonCollapsibleRowIndexes) === null || _a === void 0 ? void 0 : _a.includes(index));
            return (React__default.createElement("div", __assign({}, a11yProps, { key: keyGetter({ index: index }), role: "row", style: style, className: classNames }),
                tableRowRenderer(__assign(__assign({}, props), { style: {
                        height: rowHeight,
                    } })),
                keyGetter({ index: index }) === currentKey && isRowCollapsible && (React__default.createElement("div", { className: styles.Collapsible_content, style: { height: collapsibleContentHeight } }, collapsibleContentGetter &&
                    collapsibleContentGetter({ index: index })))));
        };
        _this.toggleRowCollapsible = function (rowKey, rowIndex) {
            var _a = _this.state, expandedRowIndex = _a.expandedRowIndex, expandedRowKey = _a.expandedRowKey;
            var key = rowKey !== expandedRowKey ? rowKey : null;
            var index = rowIndex !== expandedRowIndex ? rowIndex : null;
            _this.setState(function () { return ({
                expandedRowKey: key,
                expandedRowIndex: index,
            }); }, function () {
                var _a;
                (_a = _this.tableRef.current) === null || _a === void 0 ? void 0 : _a.recomputeRowHeights();
            });
        };
        _this.renderHeaderCheckbox = function () {
            var _a = _this.props, selectedKeys = _a.selectedKeys, keyGetter = _a.keyGetter, rowCount = _a.rowCount, onSelectionChange = _a.onSelectionChange;
            var isAllSelected = false;
            var isAnySelected = false;
            var allKeys = [];
            var selectedCount = 0;
            for (var i = 0; i < rowCount; i++) {
                var key = keyGetter({ index: i });
                allKeys.push(key);
                if (selectedKeys.includes(key)) {
                    isAllSelected = true;
                    selectedCount += 1;
                }
            }
            // Check if checkbox is indeterminate
            if (selectedCount > 0 && selectedCount < rowCount) {
                isAnySelected = true;
            }
            return (React__default.createElement(Checkbox, { className: styles.Checkbox, defaultChecked: isAllSelected, indeterminate: isAnySelected, onClick: function (e) {
                    var _a;
                    e.stopPropagation();
                    e.preventDefault();
                    var keys = [];
                    if (isAnySelected || isAllSelected) {
                        // Remove currently selected key from selectedKeys
                        // TODO: find a O(n) solution
                        keys = selectedKeys.filter(function (key) { return !allKeys.includes(key); });
                    }
                    else {
                        keys = selectedKeys.concat(allKeys);
                    }
                    onSelectionChange === null || onSelectionChange === void 0 ? void 0 : onSelectionChange(keys);
                    (_a = _this.tableRef.current) === null || _a === void 0 ? void 0 : _a.forceUpdateGrid();
                }, "data-testid": "TableHeaderCheckbox" }));
        };
        _this.renderCellCheckbox = function (data) {
            var _a = _this.props, keyGetter = _a.keyGetter, selectedKeys = _a.selectedKeys, onSelectionChange = _a.onSelectionChange;
            var key = keyGetter({ index: data.rowIndex });
            var selectedIndex = selectedKeys.indexOf(key);
            var isSelected = selectedIndex !== -1;
            return (React__default.createElement(Checkbox, { "aria-role": "checkbox", className: styles.Checkbox, checked: isSelected, onChange: function () {
                    var _a;
                    if (isSelected) {
                        var selectedKeysCopy = __spreadArray([], selectedKeys, true);
                        selectedKeysCopy.splice(selectedIndex, 1);
                        onSelectionChange === null || onSelectionChange === void 0 ? void 0 : onSelectionChange(selectedKeysCopy);
                    }
                    else {
                        onSelectionChange === null || onSelectionChange === void 0 ? void 0 : onSelectionChange(selectedKeys.concat(key));
                    }
                    (_a = _this.tableRef.current) === null || _a === void 0 ? void 0 : _a.forceUpdateGrid();
                }, "data-testid": "TableCellCheckbox" }));
        };
        _this.renderExpandableButton = function (rowIndex) {
            var _a;
            var _b = _this.props, keyGetter = _b.keyGetter, customCollapseIconProps = _b.customCollapseIconProps, onCollapsibleContentChange = _b.onCollapsibleContentChange;
            var key = keyGetter({ index: rowIndex });
            var currentKey = _this.findCurrentKeyFromExpandedRowKeys(rowIndex);
            var isExpanded = key === currentKey;
            var iconName = isExpanded
                ? (customCollapseIconProps &&
                    customCollapseIconProps.collapseIconName) ||
                    'ChevronUpIcon'
                : (customCollapseIconProps &&
                    customCollapseIconProps.unCollapseIconName) ||
                    'ChevronDownIcon';
            var onClick = function () {
                var _a;
                if (typeof onCollapsibleContentChange === 'function') {
                    onCollapsibleContentChange({
                        index: rowIndex,
                    });
                    (_a = _this.tableRef.current) === null || _a === void 0 ? void 0 : _a.recomputeRowHeights();
                }
                else {
                    _this.toggleRowCollapsible(key, rowIndex);
                }
            };
            if (_this.props.isCollapsible &&
                _this.props.collapsibleColumnCellRenderer) {
                return _this.props.collapsibleColumnCellRenderer({
                    rowIndex: rowIndex,
                    className: styles.Table_toggleIcon,
                    onClick: onClick,
                    isExpanded: isExpanded,
                });
            }
            if (_this.props.isCollapsible &&
                ((_a = _this.props.nonCollapsibleRowIndexes) === null || _a === void 0 ? void 0 : _a.includes(rowIndex))) {
                return null;
            }
            return (React__default.createElement(IconButton, __assign({ name: iconName, disableBorder: true, alt: "toggle expandable", className: styles.Table_toggleIcon, onClick: onClick, color: isExpanded ? colors.primary50 : colors.black }, customCollapseIconProps)));
        };
        _this.renderTableEmptyState = function () {
            return (React__default.createElement("div", { "data-testid": "table-empty-state-illustration", className: styles.EmptyState },
                React__default.createElement(SvgDefaultTableEmptyIllustration, null),
                React__default.createElement("div", { className: styles.EmptyState_label }, "No data available")));
        };
        _this.renderTableNoRows = function () {
            var hasError = _this.props.hasError;
            return hasError ? React__default.createElement(TableError, null) : _this.renderTableEmptyState();
        };
        return _this;
    }
    Table.prototype.componentDidMount = function () {
        if (this.props.v2) {
            console.warn('DEPRECATION WARNING: The `v2` prop is deprecated.');
        }
    };
    Table.prototype.componentDidUpdate = function (prevProps, prevState) {
        var _this = this;
        var _a, _b;
        // Closes collapsible row if data changes.
        var previousKey = prevState.expandedRowKey;
        var previousIndex = prevState.expandedRowIndex;
        var _c = this.props, keyGetter = _c.keyGetter, rowCount = _c.rowCount, expandedRowKeys = _c.expandedRowKeys;
        if (prevProps.rowCount === rowCount &&
            previousKey !== null &&
            previousIndex !== null &&
            previousKey !== keyGetter({ index: previousIndex })) {
            this.setState({
                expandedRowKey: null,
                expandedRowIndex: null,
            }, function () {
                var _a;
                (_a = _this.tableRef.current) === null || _a === void 0 ? void 0 : _a.recomputeRowHeights();
            });
        }
        if ((expandedRowKeys === null || expandedRowKeys === void 0 ? void 0 : expandedRowKeys.length) >= 0 &&
            ((_a = prevProps.expandedRowKeys) === null || _a === void 0 ? void 0 : _a.length) !== expandedRowKeys.length) {
            (_b = this.tableRef.current) === null || _b === void 0 ? void 0 : _b.recomputeRowHeights();
        }
    };
    Table.prototype.render = function () {
        var _this = this;
        var _a = this.props, height = _a.height, width = _a.width, isCollapsible = _a.isCollapsible, isSelectable = _a.isSelectable, collapsibleContentGetter = _a.collapsibleContentGetter, customRowRenderer = _a.customRowRenderer, className = _a.className, rowClassName = _a.rowClassName, children = _a.children, rowHeight = _a.rowHeight, collapsibleContentHeight = _a.collapsibleContentHeight, keyGetter = _a.keyGetter, headerHeight = _a.headerHeight, renderHeaderActions = _a.renderHeaderActions, selectedKeys = _a.selectedKeys, noRowsRenderer = _a.noRowsRenderer, tableProps = __rest(_a, ["height", "width", "isCollapsible", "isSelectable", "collapsibleContentGetter", "customRowRenderer", "className", "rowClassName", "children", "rowHeight", "collapsibleContentHeight", "keyGetter", "headerHeight", "renderHeaderActions", "selectedKeys", "noRowsRenderer"]);
        var standardRowRenderer = customRowRenderer || defaultTableRowRenderer;
        var renderer = isCollapsible ? this.collapsibleRowRenderer : standardRowRenderer;
        var tableRowRenderer = function (params) {
            var rowHeightPx = typeof rowHeight === 'function'
                ? rowHeight({ index: params.index })
                : rowHeight;
            var collapsibleContentHeightPx = typeof collapsibleContentHeight === 'function'
                ? collapsibleContentHeight({ index: params.index })
                : collapsibleContentHeight;
            return renderer(__assign(__assign({}, params), { rowHeight: rowHeightPx, collapsibleContentHeight: collapsibleContentHeightPx, collapsibleContentGetter: collapsibleContentGetter, keyGetter: keyGetter }));
        };
        var getRowHeight = function (_a) {
            var index = _a.index;
            var rowHeightPx = typeof rowHeight === 'function'
                ? rowHeight({ index: index })
                : rowHeight || 0;
            var collapsibleContentHeightPx = typeof collapsibleContentHeight === 'function'
                ? collapsibleContentHeight({ index: index })
                : collapsibleContentHeight;
            var currentKey = _this.findCurrentKeyFromExpandedRowKeys(index);
            return currentKey === keyGetter({ index: index })
                ? rowHeightPx + collapsibleContentHeightPx
                : rowHeightPx;
        };
        var getRowClassName = function (_a) {
            var index = _a.index;
            var isRowClickable = _this.props.isRowClickable;
            var isCurrentRowClickable = typeof isRowClickable === 'function'
                ? isRowClickable({ index: index })
                : !!isRowClickable;
            return clsx(_this.getRowClassNameByIndex(index), isCurrentRowClickable && styles.Table_row___clickable, typeof rowClassName === 'function'
                ? rowClassName({ index: index })
                : rowClassName);
        };
        return (React__default.createElement(AutoSizer, { disableHeight: !!height, disableWidth: !!width, className: styles.TableContainer }, function (autoSizerParams) { return (React__default.createElement(React__default.Fragment, null,
            renderHeaderActions && (React__default.createElement("div", { className: styles.RightElement, style: {
                    height: headerHeight,
                } }, renderHeaderActions)),
            React__default.createElement(Table$1, __assign({ ref: _this.tableRef, height: height ? height : autoSizerParams.height, width: width ? width : autoSizerParams.width, gridClassName: styles.Table_grid, className: clsx(styles.Table, className), rowClassName: getRowClassName, rowRenderer: tableRowRenderer, rowHeight: getRowHeight, headerHeight: headerHeight || 0, noRowsRenderer: noRowsRenderer || _this.renderTableNoRows }, tableProps),
                isCollapsible ? (React__default.createElement(Column, { label: selectedKeys.length > 0
                        ? "(" + selectedKeys.length + ")"
                        : '', dataKey: "selectedRows", width: 40, flexShrink: 0, className: styles.Table_toggleIcon, headerClassName: clsx(styles.Table_headerRow___regular, styles.Table_header), cellRenderer: function (_a) {
                        var rowIndex = _a.rowIndex;
                        return _this.renderExpandableButton(rowIndex);
                    } })) : null,
                isSelectable ? (React__default.createElement(Column, { label: "Select All", dataKey: "rowCheckbox", width: 50, flexShrink: 0, className: styles.Table_cell, headerClassName: styles.Table_header, cellRenderer: function (data) {
                        return _this.renderCellCheckbox(data);
                    }, headerRenderer: function () {
                        return _this.renderHeaderCheckbox();
                    } })) : null,
                children))); }));
    };
    Table.propTypes = propTypes;
    Table.defaultProps = __assign({}, defaultProps);
    Table.displayName = 'Table';
    return Table;
}(React__default.Component));
Table.Column = Column;

export { Table, Table as default };
